import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { setCurrentLocale } from 'store/slices/localesSlice';
import { preloadTranslations } from 'services/translationService';
const isStandalone = process.env.REACT_APP_TYPE === 'standalone';

interface LocalizationContextType {
  currentLocale: string;
  changeLocale: (locale: string) => void;
  availableLocales: string[];
}

const LocalizationContext = createContext<LocalizationContextType | null>(null);

interface LocalizationProviderProps {
  children: ReactNode;
  defaultLocale?: string;
  preloadFiles?: string[];
}

export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({
  children,
  defaultLocale = isStandalone ? 'en' : 'de',
  preloadFiles = ['_global'],
}) => {
  const dispatch = useDispatch();
  const currentLocale = useSelector(
    (state: RootState) => state.locales.currentLocale
  );

  const locale = currentLocale || defaultLocale;
  const availableLocales = ['en', 'de'];

  useEffect(() => {
    void preloadTranslations(locale, preloadFiles);
  }, [locale, preloadFiles]);

  const changeLocale = async (locale: string) => {
    // Avoid unnecessary locale changes
    if (locale === currentLocale) {
      return;
    }

    // Preload translations for new locale first, then atomically switch
    try {
      await preloadTranslations(locale, preloadFiles);
      dispatch(setCurrentLocale(locale));
      localStorage.setItem('locale', locale);
    } catch {
      // Silent fail for loading errors
    }
  };

  const contextValue: LocalizationContextType = {
    currentLocale: locale,
    changeLocale,
    availableLocales,
  };

  return (
    <LocalizationContext.Provider value={contextValue}>
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error(
      'useLocalization must be used within a LocalizationProvider'
    );
  }
  return context;
};
